import { useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  CalendarPlus, ListChecks, Lock, Map, MapPin, MoreHorizontal, Pencil, Plus, Store,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { useOutlets, useClient360Contacts } from "@/hooks/useClients";
import { CreateOutletDrawer, OUTLET_TYPE_LABELS } from "@/components/clients/CreateOutletDrawer";
import { ContactDrawer } from "@/components/clients/ContactDrawer";
import type { AddressQualityStatus, Client360Envelope, OutletRow } from "@/api/clients";

/**
 * Этап 5 «Точки» — holding outlets table (canonical CClientOutlet). List + filters
 * + «+ Додати точку» (address-picker drawer). Map = P1 (disabled toggle, не fake).
 * Row actions: Відкрити/Редагувати (drawer) · Запланувати візит → 3C (Історія) ·
 * Створити задачу → 3A (Задачі). 1С outlets render a read-only badge.
 */

const QUALITY: Record<AddressQualityStatus, { label: string; cls: string }> = {
  verified_np: { label: "НП-довідник", cls: "bg-success text-success-foreground" },
  mixed: { label: "НП частково", cls: "bg-warning text-warning-foreground" },
  manual_unverified: { label: "Ручна · перевірити", cls: "bg-warning text-warning-foreground" },
  needs_review: { label: "потребує адреси", cls: "bg-muted text-muted-foreground" },
};

export function OutletsListPage() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const id = holdingId ?? "";
  const env = useOutletContext<Client360Envelope>();
  const navigate = useNavigate();
  const q = useOutlets(id, true);

  const [filter, setFilter] = useState<"all" | "active">("all");
  const [city, setCity] = useState<string>("");
  const [drawer, setDrawer] = useState<"new" | OutletRow | null>(null);
  const [kebab, setKebab] = useState<string | null>(null);
  const [contactForOutlet, setContactForOutlet] = useState<string | null>(null);

  const data = q.data;
  const aa = data?.allowedActions;
  const clientName = env?.header?.name ?? "Клієнт";

  // Scopes for the create/edit drawer + the «new outlet contact» drawer.
  const drawerOpen = drawer !== null || contactForOutlet !== null;
  const contactsQ = useClient360Contacts(id, {}, drawerOpen);
  const scopes = contactsQ.data?.scopes ?? { holding: { id, name: clientName }, legalEntities: [], outlets: [] };
  const legalEntities = scopes.legalEntities;
  const contactOptions = useMemo(
    () => (contactsQ.data?.items ?? []).filter((c) => !c.isFormer).map((c) => ({ id: c.id, name: c.name })),
    [contactsQ.data],
  );

  const items = useMemo(() => {
    let list = data?.items ?? [];
    if (filter === "active") list = list.filter((o) => o.active);
    if (city) list = list.filter((o) => o.city === city);
    return list;
  }, [data, filter, city]);

  return (
    <div className="p-6 md:p-8">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold">
          Точки <span className="text-sm font-normal text-muted-foreground">({data?.counts.total ?? 0})</span>
        </h2>

        {/* view toggle — Карта = P1 (disabled, не fake) */}
        <div className="flex overflow-hidden rounded-md border">
          <span className="bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">Список</span>
          <button type="button" disabled title={data?.mapDisabledReason ?? "Карта — P1"} className="flex cursor-not-allowed items-center gap-1 px-3 py-1.5 text-xs font-medium text-muted-foreground/60">
            <Map className="h-3.5 w-3.5" /> Карта — скоро <Lock className="h-3 w-3" />
          </button>
        </div>

        {aa?.canCreateOutlet && (
          <button onClick={() => setDrawer("new")} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" /> Додати точку
          </button>
        )}
      </div>

      {/* filters */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <Chip on={filter === "all"} onClick={() => setFilter("all")}>Усі</Chip>
        <Chip on={filter === "active"} onClick={() => setFilter("active")}>Активні</Chip>
        {(data?.cities.length ?? 0) > 0 && (
          <select value={city} onChange={(e) => setCity(e.target.value)} className="rounded-md border border-input bg-background px-2.5 py-1.5 text-xs">
            <option value="">Усі міста</option>
            {data!.cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {q.isPending ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : data?.restricted ? (
        <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">Перегляд точок недоступний.</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-12 text-center text-sm text-muted-foreground">
          <Store className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
          Точок немає.
          {aa?.canCreateOutlet && <div className="mt-2"><button onClick={() => setDrawer("new")} className="rounded-md border px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary">+ Додати точку</button></div>}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <Th>Назва</Th><Th>Тип</Th><Th>Місто</Th><Th>Адреса</Th><Th>Контакт</Th><Th>Статус</Th><Th>Останній візит</Th><Th> </Th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className="border-t text-sm hover:bg-muted/20">
                  <td className="px-3 py-2.5 font-medium">
                    <span className="flex items-center gap-1.5">{o.name}
                      {o.official && <span title="Дзеркало з 1С" className="rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">1С</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground" title={o.type}>{OUTLET_TYPE_LABELS[o.type] ?? o.type}</td>
                  <td className="px-3 py-2.5">{o.city ?? <span className="text-muted-foreground/50">—</span>}</td>
                  <td className="px-3 py-2.5">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {o.address ?? <span className="text-muted-foreground/50">—</span>}
                      <span className={cn("rounded px-1.5 py-0.5 text-[10.5px] font-semibold", QUALITY[o.addressQualityStatus].cls)}>{QUALITY[o.addressQualityStatus].label}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{o.contactName ?? <span className="text-warning-foreground">— нема</span>}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", o.active ? "bg-success text-success-foreground" : "bg-muted text-muted-foreground")}>
                      {o.active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {o.lastVisitDays === null ? <span className="text-muted-foreground/50">—</span>
                      : <span className={cn(o.dq.includes("no_visit_90d") && "font-medium text-warning-foreground")}>{o.lastVisitDays} дн. тому{o.dq.includes("no_visit_90d") && " ⚠"}</span>}
                  </td>
                  <td className="relative px-3 py-2.5 text-right">
                    <button onClick={() => setKebab(kebab === o.id ? null : o.id)} aria-label="Дії" className="rounded p-1 hover:bg-muted">
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                    {kebab === o.id && (
                      <div className="absolute right-2 z-20 mt-1 w-52 rounded-md border bg-popover p-1 text-left shadow-lg" onMouseLeave={() => setKebab(null)}>
                        <MenuItem icon={Pencil} onClick={() => { setKebab(null); setDrawer(o); }}>Відкрити / Редагувати</MenuItem>
                        {aa?.canScheduleVisit && <MenuItem icon={CalendarPlus} onClick={() => { setKebab(null); navigate(clientRoutes.activities(id)); }}>Запланувати візит →</MenuItem>}
                        {aa?.canCreateTask && <MenuItem icon={ListChecks} onClick={() => { setKebab(null); navigate(clientRoutes.activities(id, "task_create")); }}>Створити задачу →</MenuItem>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
        <MapPin className="h-3.5 w-3.5" /> Точки — фізичні місця холдингу. Адреса з довідника НП або вручну (manual = «потребує перевірки»). Карта/маршрути — P1.
      </p>

      {drawer !== null && (
        <CreateOutletDrawer
          open
          holdingId={id}
          clientName={clientName}
          legalEntities={legalEntities}
          contacts={contactOptions}
          editOutlet={drawer === "new" ? null : drawer}
          onNewContact={(outletId) => { setDrawer(null); setContactForOutlet(outletId); }}
          onClose={() => setDrawer(null)}
        />
      )}

      <ContactDrawer
        open={contactForOutlet !== null}
        holdingId={id}
        clientName={clientName}
        scopes={scopes}
        clientType={contactsQ.data?.clientType}
        prefillScope={contactForOutlet ? { scope: "outlet", targetOutletId: contactForOutlet } : null}
        onClose={() => setContactForOutlet(null)}
      />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-semibold">{children}</th>;
}
function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={cn("rounded-md border px-3 py-1.5 text-xs font-medium", on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted")}>
      {children}
    </button>
  );
}
function MenuItem({ icon: Icon, onClick, children }: { icon: typeof Pencil; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12.5px] hover:bg-muted">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" /> {children}
    </button>
  );
}
